// ============================================================================
// PINNEDDOCS.JS — Documentación anclada (favoritos del equipo) en el dashboard
// ============================================================================
// Ocupa el espacio que antes tenían las HeroCards. Muestra los documentos
// marcados como favoritos (favorito = del tenant, compartido por el equipo),
// leídos del store (TaskStore ya carga `documents`, así que NO hay llamada
// extra a la API). Si no hay favoritos, el panel no se renderiza (el acceso a
// documentación sigue disponible desde el icono del header). Cierra con un
// enlace sutil a la sección completa de documentación.
// ============================================================================

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Star,
  FileText,
  Link as LinkIcon,
  Upload,
  ArrowUpRight,
  ChevronRight,
} from 'lucide-react';
import { m, useReducedMotion } from 'framer-motion';
import { useTaskStore } from '../store/TaskStore';
import { MAX_PINNED_DOCS } from '../services/documentationService';
import { smartOpenLink } from '../utils/smartLink';

function typeMeta(tipo) {
  switch (tipo) {
    case 'enlace': return { Icon: LinkIcon, label: 'Enlace', cls: 'enlace' };
    case 'pdf': return { Icon: Upload, label: 'PDF', cls: 'pdf' };
    default: return { Icon: FileText, label: 'Documento', cls: 'documento' };
  }
}

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06, delayChildren: 0.03 } },
};
const cardVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] } },
};

function PinnedDocs({ onOpenDocumentation }) {
  const navigate = useNavigate();
  const documents = useTaskStore((s) => s.documents);
  const reduce = useReducedMotion();

  const favorites = useMemo(
    () => (documents || []).filter(d => d.favorito).slice(0, MAX_PINNED_DOCS),
    [documents]
  );

  // Sin anclados: el panel no se muestra (el acceso a docs vive en el header).
  if (favorites.length === 0) return null;

  const openDoc = (doc) => {
    if (doc.tipo === 'enlace' && doc.url) {
      // Mismo dominio → navegación interna fluida; externo → pestaña nueva.
      smartOpenLink(doc.url, navigate);
      return;
    }
    navigate(`/task/documentation/view/${doc.id}`);
  };

  const goToDocs = () => {
    if (onOpenDocumentation) onOpenDocumentation();
    else navigate('/task/documentation');
  };

  return (
    <section className="pinned-docs" aria-label="Documentación anclada">
      <div className="pinned-docs-head">
        <Star size={13} className="pinned-docs-star" />
        <span className="pinned-docs-label">ANCLADOS</span>
        <span className="pinned-docs-count">{favorites.length}</span>
      </div>

      <m.div
        className="pinned-docs-row"
        variants={reduce ? undefined : containerVariants}
        initial={reduce ? false : 'hidden'}
        animate={reduce ? false : 'visible'}
      >
        {favorites.map(doc => {
          const { Icon, label, cls } = typeMeta(doc.tipo);
          const firstCat = (doc.categorias || [])[0];
          const isLink = doc.tipo === 'enlace';
          return (
            <m.button
              data-motion-fallback
              key={doc.id}
              type="button"
              className="pdoc-card"
              variants={reduce ? undefined : cardVariants}
              whileHover={reduce ? undefined : { y: -2 }}
              onClick={() => openDoc(doc)}
              title={doc.titulo}
            >
              <span className={`pdoc-ico ${cls}`}>
                <Icon size={16} />
              </span>
              <span className="pdoc-text">
                <span className="pdoc-title">
                  {doc.titulo}
                  {isLink && <ArrowUpRight size={13} className="pdoc-ext" />}
                </span>
                <span className="pdoc-meta">
                  {label}
                  {firstCat && (
                    <>
                      {' · '}
                      <span className="pdoc-cat" style={{ color: firstCat.color }}>
                        <span className="pdoc-cat-dot" style={{ background: firstCat.color }} />
                        {firstCat.nombre}
                      </span>
                    </>
                  )}
                </span>
              </span>
            </m.button>
          );
        })}

        <button type="button" className="pdoc-all" onClick={goToDocs} title="Ir a la documentación">
          <span>Ver toda<br />la documentación</span>
          <ChevronRight size={16} />
        </button>
      </m.div>
    </section>
  );
}

export default PinnedDocs;
